import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, FileText, Target, UserRound, Users } from "lucide-react";
import { useFacility } from "../../../context/FacilityContext";
import { Avatar, TagInput } from "../../components/crm/ui";
import {
  BUDGETS,
  Field,
  FormSection,
  GhostButton,
  PRIORITIES,
  PrefixInput,
  SOURCES,
  SelectInput,
  TITLES,
  TextInput,
  TimeInput,
  apiError,
  apiGet,
  apiPost,
  apiPut,
  fmtPhone,
  fullName,
  initialsOf,
  isEmail,
  isoDay,
  phone10,
  priorityKey,
  priorityLabel,
  sourceKey,
  sourceLabel,
  statusKeyOf,
  statusOptions,
  toLead,
} from "../../components/crm/leadsKit";
import ContactPicker, { toPickerContact } from "../../components/crm/ContactPicker";
import { PrimaryButton } from "../../components/ui";
import { SearchSelect } from "../../components/SearchSelect";
import { InlineLoader, PageLoader } from "../../components/PageLoader";

/**
 * CRM → Leads → add / edit (design: screens/crm/LeadForm.tsx). `/crm/leads/new`
 * (optionally `?contact=<id>`) and `/crm/leads/edit/:id`.
 *
 * Real data and actions:
 *   lead to edit ................................ GET /api/leads/:id
 *   create / save ............................... POST /api/leads, PUT /api/leads/:id
 *   existing contacts, phone match, ?contact= ... GET /api/contacts, GET /api/contacts/:id
 *   the picked contact's leads, open-lead warning  GET /api/leads?contact_id= (item 153)
 *   follow-up date and time ..................... next_follow_up_at "YYYY-MM-DDTHH:MM" (item 144)
 *   statuses, staff, doctors, services, tags .... GET /api/lead-statuses, /api/staff/assignable-users,
 *                                                 /api/staff?role=doctor, /api/treatments, /api/tags
 *   new tags .................................... POST /api/tags
 *   facilities .................................. FacilityContext
 * "Filled by Olli" comes from the assistant's prefill (item 145).
 */

const blankErrors = { firstName: "", phone: "", email: "", altPhone: "" };
const OPEN_EXCLUDED = ["won", "lost", "junk"];
// next_follow_up_at → "HH:MM" on the clinic's clock. A bare "2026-10-29 16:30:00"
// is already wall-clock time; an ISO instant is read in the browser's zone,
// as next_follow_up_date already is.
const followUpTime = (value) => {
  if (!value) return "";
  const bare = String(value).match(/^\d{4}-\d{2}-\d{2}[ T](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/);
  if (bare) return `${bare[1]}:${bare[2]}`;
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? "" : `${String(at.getHours()).padStart(2, "0")}:${String(at.getMinutes()).padStart(2, "0")}`;
};

// Keyed by id: /new and /edit/:id share this component, so without a key the
// form would open showing the previous lead's values.
export default function LeadForm() {
  const { id } = useParams();
  return <LeadFormPage key={id || "new"} />;
}

function LeadFormPage() {
  const fac = useFacility();
  const navigate = useNavigate();
  // Olli's prefill (item 145): the fields it understood, from its "Open the new
  // lead form" link. Nothing is saved until the person saves.
  const [olliFilled, setOlliFilled] = useState(false);
  const olliPrefill = useLocation().state?.olliPrefill || null;
  const { id: editId } = useParams();
  const [params, setParams] = useSearchParams();
  const contactParam = params.get("contact") ?? "";
  const [editingLead, setEditingLead] = useState(null);
  const [editMissing, setEditMissing] = useState(false);
  const [lockedContact, setLockedContact] = useState(null);
  const [lockedFor, setLockedFor] = useState(""); // the ?contact= id lockedContact was loaded for
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [pickerLeads, setPickerLeads] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [staff, setStaff] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [services, setServices] = useState([]);
  const [tagRows, setTagRows] = useState([]);
  const [contactMode, setContactMode] = useState(contactParam ? "existing" : "new");
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedLeadsState, setSelectedLeadsState] = useState(null); // { contactId, rows }
  const [contactPickError, setContactPickError] = useState("");
  const [title, setTitle] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [altPhone, setAltPhone] = useState("");
  const [source, setSource] = useState("Manual entry");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [facility, setFacility] = useState(fac.currentFacility ? String(fac.currentFacility.id) : "");
  const [service, setService] = useState("");
  const [doctor, setDoctor] = useState("");
  const [budget, setBudget] = useState("");
  const [tags, setTags] = useState([]);
  const [assignee, setAssignee] = useState("");
  const [followupDate, setFollowupDate] = useState("");
  const [followupTime, setFollowupTime] = useState("10:00");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState(blankErrors);
  const [saveError, setSaveError] = useState("");
  const [loading, setLoading] = useState(false);
  const [matchedContactCandidate, setMatchedContactCandidate] = useState(null);

  useEffect(() => {
    apiGet("/api/lead-statuses").then((r) => setStatuses(statusOptions(r?.data)));
    apiGet("/api/staff/assignable-users").then((r) => setStaff((r?.data || []).filter((u) => u.user_id && u.name).map((u) => ({ id: u.user_id, name: u.name }))));
    apiGet("/api/staff", { is_active: true, role: "doctor" }).then((r) => setDoctors((r?.data || []).map((d) => ({ id: d.id, name: d.user_name || `${d.first_name || ""} ${d.last_name || ""}`.trim() })).filter((d) => d.name)));
    apiGet("/api/treatments").then((r) => { const list = Array.isArray(r?.data) ? r.data : r?.data?.treatments || []; setServices([...new Set(list.map((t) => t.name).filter(Boolean))]); });
    apiGet("/api/tags").then((r) => setTagRows(r?.data || []));
    if (!editId) {
      apiGet("/api/contacts", { limit: 2000 }).then((r) => { setContacts((r?.data || []).map(toPickerContact)); setContactsLoading(false); });
      apiGet("/api/leads", { limit: 2000 }).then((r) => setPickerLeads(r?.success ? (r.data || []).map((l) => ({ contactId: l.contact_id, createdAt: new Date(l.created_at).getTime(), statusKey: l.status_key || l.status })) : null));
    }
  }, [editId]);

  // Status names for the picker's leads once statuses are known.
  const pickerLeadsNamed = useMemo(() => (pickerLeads ? pickerLeads.map((l) => ({ ...l, status: statuses.find((s) => s.key === l.statusKey)?.name || "New" })) : null), [pickerLeads, statuses]);

  // Default status for a new lead: the clinic's default stage.
  useEffect(() => {
    if (editId || status || !statuses.length) return;
    setStatus((statuses.find((s) => s.isDefault) || statuses[0]).name);
  }, [statuses, editId, status]);

  // ?contact=<id>: the lead goes to that contact.
  useEffect(() => {
    if (!contactParam) { setLockedContact(null); return undefined; }
    let alive = true;
    apiGet(`/api/contacts/${contactParam}`).then((r) => {
      if (!alive) return;
      const c = r?.success ? toPickerContact(r.data) : null;
      setLockedContact(c);
      setLockedFor(contactParam);
      if (c && !editId) { setContactMode("existing"); setSelectedContact(c); }
    });
    return () => { alive = false; };
  }, [contactParam, editId]);
  const lockedLoading = !!contactParam && lockedFor !== contactParam;

  // The lead being edited.
  useEffect(() => {
    if (!editId) return;
    apiGet(`/api/leads/${editId}`).then((r) => {
      if (!r?.success) { setEditMissing(true); return; }
      const d = r.data;
      setEditingLead(toLead(d));
      setTitle(d.contact_title || "");
      setFirstName(d.first_name || "");
      setLastName(d.last_name || "");
      setPhone(phone10(d.phone));
      setEmail(d.email || "");
      setAltPhone(d.alternate_phone ? phone10(d.alternate_phone) : "");
      setSource(sourceLabel(d.source));
      setPriority(priorityLabel(d.priority));
      setFacility(d.facility_id ? String(d.facility_id) : "");
      setService(d.interested_service || "");
      setDoctor(d.preferred_doctor_id ? String(d.preferred_doctor_id) : "");
      setBudget(d.budget_range || "");
      setTags((d.tags || []).map((t) => t.name).filter(Boolean));
      setAssignee(d.assigned_to ? String(d.assigned_to) : "");
      setNotes(d.notes || "");
      setFollowupDate(d.next_follow_up_date ? isoDay(new Date(d.next_follow_up_date).getTime()) : "");
      // A follow-up saved as a day only keeps no time until one is picked.
      setFollowupTime(d.next_follow_up_date ? followUpTime(d.next_follow_up_at) : "10:00");
    });
  }, [editId]);
  useEffect(() => {
    if (editingLead && statuses.length) setStatus(statuses.find((s) => s.key === editingLead.statusKey)?.name || editingLead.statusKey);
  }, [editingLead, statuses]);

  // A contact that already has this phone number.
  useEffect(() => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length !== 10) { setMatchedContactCandidate(null); return; }
    let alive = true;
    apiGet("/api/contacts", { search: digits, limit: 5 }).then((r) => {
      if (!alive) return;
      const hit = (r?.data || []).find((c) => phone10(c.phone) === digits);
      setMatchedContactCandidate(hit ? toPickerContact(hit) : null);
    });
    return () => { alive = false; };
  }, [phone]);
  const matchedContact = matchedContactCandidate && editingLead && String(matchedContactCandidate.id) === String(editingLead.contactId) ? null : matchedContactCandidate;

  // Leads of the picked contact: how many, and whether one is still open.
  useEffect(() => {
    if (!selectedContact) { setSelectedLeadsState(null); return; }
    let alive = true;
    apiGet("/api/leads", { contact_id: selectedContact.id, limit: 100 }).then((r) => {
      if (alive) setSelectedLeadsState({ contactId: selectedContact.id, rows: r?.success ? r.data || [] : null });
    });
    return () => { alive = false; };
  }, [selectedContact]);
  // Only the picked contact's leads; null while they load (or when the call failed).
  const selectedLeadsLoading = !!selectedContact && selectedLeadsState?.contactId !== selectedContact.id;
  const selectedLeads = selectedLeadsLoading ? null : selectedLeadsState?.rows ?? null;

  useEffect(() => {
    if (!matchedContact || lockedContact || (!editingLead && contactMode !== "new")) return;
    setFirstName((current) => current || matchedContact.firstName);
    setLastName((current) => current || matchedContact.lastName);
    setEmail((current) => current || matchedContact.email);
  }, [matchedContact, lockedContact, editingLead, contactMode]);

  useEffect(() => {
    if (!olliPrefill) return;
    const f = olliPrefill;
    if (f.first_name) setFirstName(f.first_name);
    if (f.last_name) setLastName(f.last_name);
    if (f.phone) setPhone(String(f.phone).replace(/[^\d]/g, "").slice(-10));
    if (f.email) setEmail(f.email);
    if (f.interested_service) setService(f.interested_service);
    if (f.notes) setNotes(f.notes);
    setOlliFilled(true);
  }, [olliPrefill]);
  const clearOlli = () => { setFirstName(""); setLastName(""); setPhone(""); setService(""); setNotes(""); setSource("Manual entry"); setOlliFilled(false); };

  const clearContactLock = () => {
    const next = new URLSearchParams(params);
    next.delete("contact");
    setParams(next, { replace: true });
  };

  const startNewContact = (query) => {
    const rawDigits = query.replace(/\D/g, "");
    const digits = rawDigits.length === 12 && rawDigits.startsWith("91") ? rawDigits.slice(2) : rawDigits;
    setContactMode("new");
    setSelectedContact(null);
    setContactPickError("");
    if (digits.length === 10 && /^\+?\d[\d\s()-]*$/.test(query)) setPhone(digits);
    else if (query.includes("@")) setEmail(query.trim());
    else if (query.trim()) {
      const [first, ...rest] = query.trim().split(/\s+/);
      setFirstName(first);
      setLastName(rest.join(" "));
    }
  };

  /** Tag names → tag ids, creating the clinic's missing tags. */
  const tagIds = async () => {
    const byName = new Map(tagRows.map((t) => [String(t.name).toLowerCase(), t.id]));
    const ids = [];
    for (const name of tags) {
      let tagId = byName.get(name.toLowerCase());
      if (!tagId) tagId = (await apiPost("/api/tags", { name, tag_type: "lead", color: "#667eea" }).catch(() => null))?.data?.id;
      if (tagId) ids.push(tagId);
    }
    return ids;
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaveError("");
    const nextErrors = { ...blankErrors };
    if (!editingLead && contactMode === "existing") {
      if (!selectedContact) {
        setContactPickError("Pick a contact or switch to New contact.");
        requestAnimationFrame(() => document.getElementById("contact-picker")?.scrollIntoView({ behavior: "smooth", block: "center" }));
        return;
      }
      setContactPickError("");
    } else {
      if (!firstName.trim()) nextErrors.firstName = "Enter the contact's first name.";
      if (!/^\d{10}$/.test(phone.replace(/\D/g, ""))) nextErrors.phone = "Enter a valid 10-digit Indian phone number.";
      if (email.trim() && !isEmail(email)) nextErrors.email = "Enter a valid email address.";
      if (altPhone.trim() && !/^\d{10}$/.test(altPhone.replace(/\D/g, ""))) nextErrors.altPhone = "Enter a valid 10-digit phone number.";
    }
    setErrors(nextErrors);
    const firstError = Object.keys(nextErrors).find((key) => nextErrors[key]);
    if (firstError) {
      requestAnimationFrame(() => document.getElementById(firstError)?.scrollIntoView({ behavior: "smooth", block: "center" }));
      return;
    }
    setLoading(true);
    try {
      const useExisting = !editingLead && contactMode === "existing" && selectedContact;
      const contact = useExisting
        ? { contact_id: selectedContact.id, first_name: selectedContact.firstName, last_name: selectedContact.lastName || null, phone: selectedContact.phone || null, email: selectedContact.email || null }
        : { title: title || null, first_name: firstName.trim(), last_name: lastName.trim() || null, phone: phone.replace(/\D/g, "").slice(-10), email: email.trim() || null, alternate_phone: altPhone.replace(/\D/g, "").slice(-10) || null };
      const body = {
        ...contact,
        source: sourceKey(source) || "manual",
        status: statusKeyOf(statuses, status) || undefined,
        priority: priorityKey(priority),
        facility_id: facility ? Number(facility) : null,
        interested_service: service.trim() || null,
        preferred_doctor_id: doctor ? Number(doctor) : null,
        budget_range: budget || null,
        tags: await tagIds(),
        assigned_to: assignee ? Number(assignee) : null,
        // The day, with its time when one is picked (item 144).
        next_follow_up_at: followupDate ? (followupTime ? `${followupDate}T${followupTime}` : followupDate) : null,
        notes: notes.trim() || null,
      };
      if (editingLead) {
        delete body.contact_id;
        await apiPut(`/api/leads/${editingLead.id}`, body);
        navigate(`/crm/leads/${editingLead.id}`);
        return;
      }
      const r = await apiPost("/api/leads", body);
      navigate(r?.data?.id ? `/crm/leads/${r.data.id}` : "/crm/leads");
    } catch (e) {
      const message = apiError(e, editingLead ? "Couldn't save the lead. Try again." : "Couldn't create the lead. Try again.");
      if (e?.response?.status === 409 && !(contactMode === "existing" && !editingLead)) setErrors((current) => ({ ...current, phone: message }));
      else setSaveError(message);
    } finally {
      setLoading(false);
    }
  };

  if (editId && editMissing) return <main className="mx-auto flex max-w-4xl flex-col gap-6 px-8 py-9 animate-fade-in"><h1 className="text-h1 font-semibold">Lead not found</h1><p className="text-small text-muted">This lead could not be found.</p><Link to="/crm/leads" className="flex w-fit items-center gap-1.5 text-small font-medium text-muted hover:text-ink"><ArrowLeft size={16} strokeWidth={1.75} />Back to leads</Link></main>;
  if (editId && !editingLead) return <main className="mx-auto flex max-w-4xl flex-col gap-6 px-8 py-9"><PageLoader label="Loading lead…" /></main>;

  const titleOptions = [{ value: "", label: "Select title" }, ...TITLES.map((item) => ({ value: item, label: item }))];
  const phoneChange = (e) => { const next = e.target.value.replace(/\D/g, "").slice(0, 10); if (matchedContact && next !== matchedContact.phone) { if (firstName === matchedContact.firstName) setFirstName(""); if (lastName === matchedContact.lastName) setLastName(""); if (email === matchedContact.email) setEmail(""); } setPhone(next); };
  const openLead = selectedLeads?.find((lead) => !OPEN_EXCLUDED.includes(lead.status));
  const statusNameOf = (row) => statuses.find((s) => s.key === (row.status_key || row.status))?.name || row.status_key || row.status;

  return <main className="mx-auto flex max-w-4xl flex-col gap-6 px-8 py-9 animate-fade-in">
    <header className="flex flex-col gap-3"><Link to={editingLead ? `/crm/leads/${editingLead.id}` : "/crm/leads"} className="flex w-fit items-center gap-1.5 text-small font-medium text-muted hover:text-ink"><ArrowLeft size={16} strokeWidth={1.75} />{editingLead ? "Back to lead" : "Leads"}</Link><div><h1 className="text-h1 font-semibold">{editingLead ? "Edit lead" : "Add lead"}</h1>{editingLead && <p className="mt-1 text-small text-muted">{fullName(editingLead)} · {editingLead.ref}</p>}</div></header>
    <form onSubmit={submit} noValidate className="flex flex-col gap-5">
      {olliFilled && <div className="flex items-center justify-between gap-3 rounded-md bg-sky-soft px-3 py-2 text-small text-ink"><span>Filled by Olli. Check the details before you save.</span><button type="button" onClick={clearOlli} className="shrink-0 text-small font-semibold text-sky-deep">Clear</button></div>}
      <FormSection icon={<UserRound size={18} strokeWidth={1.75} />} title="Contact">
        {editingLead ? (
          lockedContact ? (
            <div className="col-span-2 flex items-center justify-between gap-4 rounded-sm bg-mist px-4 py-3">
              <div>
                <p className="text-small font-semibold">{fullName(lockedContact)}</p>
                <p className="mt-1 text-caption text-muted">{fmtPhone(lockedContact.phone)}{lockedContact.email ? ` · ${lockedContact.email}` : ""}</p>
                <p className="mt-1 text-caption text-muted">This lead will be added to the existing contact.</p>
              </div>
              <button type="button" onClick={clearContactLock} className="shrink-0 text-small font-semibold text-muted underline hover:text-ink">Change</button>
            </div>
          ) : (
            <>
              <Field id="title" label="Title"><SearchSelect id="title" value={title} onChange={setTitle} placeholder="Select title" options={titleOptions} /></Field>
              <Field id="firstName" label="First name *" error={errors.firstName}><TextInput id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Enter first name" invalid={!!errors.firstName} /></Field>
              <Field id="lastName" label="Last name" optional><TextInput id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Enter last name" /></Field>
              <Field id="phone" label="Phone *" error={errors.phone}><PrefixInput id="phone" prefix="+91" value={phone} onChange={phoneChange} placeholder="Enter 10-digit phone number" inputMode="numeric" invalid={!!errors.phone} /></Field>
              <Field id="email" label="Email" optional error={errors.email}><TextInput id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter email address" invalid={!!errors.email} /></Field>
              <Field id="altPhone" label="Alternate phone" optional error={errors.altPhone}><PrefixInput id="altPhone" prefix="+91" value={altPhone} onChange={(e) => setAltPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="Enter alternate phone" inputMode="numeric" invalid={!!errors.altPhone} /></Field>
              {matchedContact && <div className="col-span-2 flex items-center justify-between gap-3 rounded-sm bg-sky-soft px-4 py-3 text-small"><span><strong>{fullName(matchedContact)}</strong> already has this number.</span><Link to={`/crm/contacts/${matchedContact.id}`} className="shrink-0 font-semibold underline">View contact</Link></div>}
            </>
          )
        ) : (
          <>
            <div className="col-span-2 flex w-fit rounded-full bg-mist p-1 animate-fade-in" role="tablist" aria-label="Contact type">
              {[["existing", "Existing contact"], ["new", "New contact"]].map(([mode, label]) => (
                <button key={mode} type="button" role="tab" aria-selected={contactMode === mode} onClick={() => { setContactMode(mode); setContactPickError(""); }} className={`rounded-full px-4 py-2 text-small font-semibold transition-colors ${contactMode === mode ? "bg-paper text-ink shadow-soft" : "text-muted hover:text-ink"}`}>
                  {label}
                </button>
              ))}
            </div>
            {contactMode === "existing" ? (
              <div id="contact-picker" className="col-span-2 flex flex-col gap-3 animate-fade-in">
                {selectedContact ? (
                  <>
                    <div className="flex items-center gap-3 rounded-sm bg-mist px-4 py-3">
                      <Avatar name={fullName(selectedContact)} initials={initialsOf(selectedContact)} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-small font-semibold">{fullName(selectedContact)}</p>
                        <p className="mt-0.5 truncate text-caption text-muted">{fmtPhone(selectedContact.phone)}{selectedContact.email ? ` · ${selectedContact.email}` : ""}</p>
                        <p className="mt-1 text-caption text-muted">{selectedLeadsLoading ? <InlineLoader size={14} /> : selectedLeads ? selectedLeads.length : "—"} leads so far</p>
                      </div>
                      <Link to={`/crm/contacts/${selectedContact.id}`} className="shrink-0 text-caption font-semibold text-muted underline hover:text-ink">View contact</Link>
                      <button type="button" onClick={() => { setSelectedContact(null); setContactPickError(""); }} className="shrink-0 text-small font-semibold text-muted underline hover:text-ink">Change</button>
                    </div>
                    {openLead ? <p className="text-caption text-muted">{fullName(selectedContact)} already has an open lead <Link to={`/crm/leads/${openLead.id}`} className="font-semibold underline hover:text-ink">#{openLead.id}</Link> ({statusNameOf(openLead)}). A second open lead with the same phone or email can't be added.</p> : null}
                  </>
                ) : lockedLoading ? (
                  <div className="flex justify-center py-4"><InlineLoader /></div>
                ) : (
                  <>
                    <ContactPicker loading={contactsLoading} contacts={contacts} leads={pickerLeadsNamed} onSelect={(contact) => { setSelectedContact(contact); setContactPickError(""); }} onCreateNew={startNewContact} />
                    {contactPickError && <p className="text-caption text-danger">{contactPickError}</p>}
                  </>
                )}
              </div>
            ) : (
              <div className="col-span-2 grid grid-cols-2 gap-x-5 gap-y-4 animate-fade-in">
                <Field id="title" label="Title"><SearchSelect id="title" value={title} onChange={setTitle} placeholder="Select title" options={titleOptions} /></Field>
                <Field id="firstName" label="First name *" error={errors.firstName}><TextInput id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Enter first name" invalid={!!errors.firstName} /></Field>
                <Field id="lastName" label="Last name" optional><TextInput id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Enter last name" /></Field>
                <Field id="phone" label="Phone *" error={errors.phone}><PrefixInput id="phone" prefix="+91" value={phone} onChange={phoneChange} placeholder="Enter 10-digit phone number" inputMode="numeric" invalid={!!errors.phone} /></Field>
                <Field id="email" label="Email" optional error={errors.email}><TextInput id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter email address" invalid={!!errors.email} /></Field>
                <Field id="altPhone" label="Alternate phone" optional error={errors.altPhone}><PrefixInput id="altPhone" prefix="+91" value={altPhone} onChange={(e) => setAltPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="Enter alternate phone" inputMode="numeric" invalid={!!errors.altPhone} /></Field>
                {matchedContact && <div className="col-span-2 flex items-center justify-between gap-3 rounded-sm bg-sky-soft px-4 py-3 text-small"><span><strong>{fullName(matchedContact)}</strong> already has this number.</span><button type="button" onClick={() => { setSelectedContact(matchedContact); setContactMode("existing"); setContactPickError(""); }} className="shrink-0 font-semibold underline">Use this contact</button></div>}
              </div>
            )}
          </>
        )}
      </FormSection>

      <FormSection icon={<Target size={18} strokeWidth={1.75} />} title="Lead details">
        <Field id="source" label="Source"><SearchSelect id="source" value={source} onChange={setSource} placeholder="Select source" options={SOURCES} /></Field>
        <Field id="status" label="Status"><SearchSelect id="status" value={status} onChange={setStatus} placeholder="Select status" options={statuses.map((s) => s.name)} /></Field>
        <Field id="priority" label="Priority"><SelectInput id="priority" value={priority} onChange={(e) => setPriority(e.target.value)} placeholder="Select priority" options={[...PRIORITIES]} /></Field>
        <Field id="facility" label="Facility"><SearchSelect id="facility" value={facility} onChange={setFacility} placeholder="Select facility" options={fac.facilities.map((f) => ({ value: String(f.id), label: f.name }))} searchable /></Field>
        <Field id="service" label="Interested service" optional><SearchSelect id="service" value={service} onChange={setService} placeholder="e.g. Dental check-up" options={service && !services.includes(service) ? [...services, service] : services} onCreate={setService} createLabel="Use" /></Field>
        <Field id="doctor" label="Preferred doctor" optional><SearchSelect id="doctor" value={doctor} onChange={setDoctor} placeholder="Any doctor" options={doctors.map((d) => ({ value: String(d.id), label: d.name }))} searchable /></Field>
        <Field id="budget" label="Budget range" optional><SelectInput id="budget" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="Select budget" options={budget && !BUDGETS.includes(budget) ? [...BUDGETS, budget] : BUDGETS} /></Field>
        <div className="col-span-2"><Field id="tags" label="Tags" optional><TagInput id="tags" value={tags} onChange={setTags} suggestions={tagRows.map((t) => t.name).filter(Boolean)} /></Field></div>
      </FormSection>

      <FormSection icon={<Users size={18} strokeWidth={1.75} />} title="Assignment and follow-up">
        <Field id="assignee" label="Assign to" optional><SearchSelect id="assignee" value={assignee} onChange={(value) => setAssignee(value === "unassigned" ? "" : value)} placeholder="Unassigned" options={[...staff.map((s) => ({ value: String(s.id), label: s.name })), { value: "unassigned", label: "Unassigned" }]} searchable /></Field>
        <div className="grid grid-cols-2 gap-3"><Field id="followupDate" label="Next follow-up date" optional><TextInput id="followupDate" type="date" value={followupDate} min={isoDay(Date.now())} onChange={(e) => setFollowupDate(e.target.value)} /></Field><Field id="followupTime" label="Time" optional><TimeInput id="followupTime" size="sm" value={followupTime} onChange={setFollowupTime} /></Field></div>
      </FormSection>

      <FormSection icon={<FileText size={18} strokeWidth={1.75} />} title="Notes">
        <div className="col-span-2"><Field id="notes" label="Notes" optional><textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the team should know about this lead" rows={4} className="w-full resize-y rounded-sm border border-line bg-paper px-4 py-3 text-small text-ink outline-none placeholder:text-muted/70 focus:border-ink focus:ring-2 focus:ring-ink/10" /></Field></div>
      </FormSection>

      <footer className="sticky bottom-4 z-10 flex items-center justify-end gap-2 rounded-lg bg-paper p-3 shadow-soft ring-1 ring-line/60">{saveError && <p role="alert" className="mr-auto px-2 text-small text-danger">{saveError}</p>}<GhostButton type="button" onClick={() => (editingLead ? navigate(`/crm/leads/${editingLead.id}`) : navigate(-1))}>Cancel</GhostButton><PrimaryButton type="submit" loading={loading} loadingLabel={editingLead ? "Saving…" : "Creating lead…"}>{editingLead ? "Save changes" : "Create lead"}</PrimaryButton></footer>
    </form>
  </main>;
}
